import { computed, onMounted, onUnmounted, ref } from "vue";
import provideHook from "@/lib/provideHook";

import CONSTANTS from "@/Constants";

// The sanctioned reactive screen-size check (see .claude/rules/frontend.md).
// `type` mirrors the Tailwind `md:` breakpoint via CONSTANTS.BREAKPOINTS.md,
// so JS and stylesheet agree on where "mobile" ends. Unlike the deprecated
// `breakpoints()` mixin, the returned refs update on window resize.
export const useBreakpoints = provideHook(() => {
  const windowWidth = ref(window.innerWidth);
  const windowHeight = ref(window.innerHeight);

  const measure = () => {
    windowWidth.value = window.innerWidth;
    windowHeight.value = window.innerHeight;
  };

  // Chromium under device emulation can dispatch the window's resize event
  // while innerWidth still reports a transitional layout, and the visual
  // viewport's own resize does not always follow with the settled size. A
  // media query is evaluated against the settled layout, so its change event
  // is the one signal that always arrives once the breakpoint is crossed.
  // Older WebKit lists know only addListener; those are left to the resize path.
  const limit = CONSTANTS.BREAKPOINTS.md - 1;
  const crossings =
    typeof window.matchMedia === "function"
      ? [`(max-width: ${limit}px)`, `(max-height: ${limit}px)`]
          .map((query) => window.matchMedia(query))
          .filter((list) => typeof list?.addEventListener === "function")
      : [];

  onMounted(() => {
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    crossings.forEach((query) => query.addEventListener("change", measure));
  });
  onUnmounted(() => {
    window.removeEventListener("resize", measure);
    window.visualViewport?.removeEventListener("resize", measure);
    crossings.forEach((query) => query.removeEventListener("change", measure));
  });

  const type = computed(() =>
    windowWidth.value < CONSTANTS.BREAKPOINTS.md ? "mobile" : "desktop"
  );

  // A phone held sideways is wider than `md` and a tablet held upright is
  // narrower, yet both are the same handheld device. Judging by the shorter
  // side keeps a decision stable across rotation, where `type` flips.
  const isHandheld = computed(
    () =>
      Math.min(windowWidth.value, windowHeight.value) < CONSTANTS.BREAKPOINTS.md
  );

  const width = computed(() => windowWidth.value);

  return { width, type, isHandheld };
});
